import type { Scope } from './scope-stack.js';

export type Command = {
  id: string;
  title: string;
  /** Scope que puede resolverlo. Un comando de `list` no se dispara desde el editor. */
  scope: Scope;
  /** Acorde único (`a`) o secuencia (`g m`), en forma canónica de `chordFromEvent`. */
  keys: string;
  /** Fuera de la paleta: acciones de navegación pura o duplicados de otra entrada. */
  hidden?: boolean;
  group?: string;
  run: () => void;
};

/**
 * Registro central que alimenta a la vez los atajos y la paleta (diseño §11), para que
 * no puedan divergir: lo que la paleta muestra es exactamente lo que el teclado ejecuta.
 */
export class CommandRegistry {
  private commands = new Map<string, Command>();
  private listeners = new Set<() => void>();

  register(command: Command): () => void {
    this.commands.set(command.id, command);
    this.emit();
    return () => {
      if (this.commands.delete(command.id)) this.emit();
    };
  }

  registerAll(commands: readonly Command[]): () => void {
    const undo = commands.map((command) => this.register(command));
    return () => {
      for (const off of undo) off();
    };
  }

  /** Para la paleta: solo lo alcanzable desde los scopes activos y no oculto. */
  visible(chain: readonly Scope[]): Command[] {
    return [...this.commands.values()].filter(
      (command) => command.hidden !== true && chain.includes(command.scope),
    );
  }

  all(): Command[] {
    return [...this.commands.values()];
  }

  /**
   * `escape` lo resuelve únicamente el scope superior y no cae en cascada: pulsar Esc
   * con el popup de menciones abierto cierra ese popup, nunca el panel de detrás.
   * El resto de acordes sí busca hacia fuera, para que `a` siga asignando desde el panel.
   */
  resolve(keys: string, chain: readonly Scope[]): Command | null {
    if (chain.length === 0) return null;
    const searched = keys === 'escape' ? chain.slice(0, 1) : chain;
    for (const scope of searched) {
      const match = this.find(keys, scope);
      if (match !== null) return match;
    }
    return null;
  }

  /** Prefijo válido de una secuencia (`g` cuando existe `g m`): el buffer debe esperar. */
  isSequencePrefix(prefix: string, chain: readonly Scope[]): boolean {
    return [...this.commands.values()].some(
      (command) => chain.includes(command.scope) && command.keys.startsWith(`${prefix} `),
    );
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private find(keys: string, scope: Scope): Command | null {
    for (const command of this.commands.values()) {
      if (command.scope === scope && command.keys === keys) return command;
    }
    return null;
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}
